export enum NetworkAnnonymityLevel {
  None = 0,
  Relay = 10,
  Proxy = 20,
  Hosting = 30,
  VPN = 40,
  TOR = 50,
  All = 2147483647,
}

export interface ILineitemNetworkAnnonymity {
  /** Levels to target. Takes precedence over the legacy threshold below. */
  anonymityLevelsAllowed?: NetworkAnnonymityLevel[];
  /** Legacy maximum threshold - the DSP only falls back to it when the above is absent. */
  maxAllowableAnonymity?: NetworkAnnonymityLevel;
}

export const NETWORK_ANONYMITY_LABEL = "Anonymity filtering";

export const NETWORK_ANONYMITY_LEVELS = [
  NetworkAnnonymityLevel.None,
  NetworkAnnonymityLevel.Relay,
  NetworkAnnonymityLevel.Proxy,
  NetworkAnnonymityLevel.Hosting,
  NetworkAnnonymityLevel.VPN,
  NetworkAnnonymityLevel.TOR,
];

export const NETWORK_ANONYMITY_LABELS: Record<NetworkAnnonymityLevel, string> =
  {
    [NetworkAnnonymityLevel.None]: "No anonymity",
    [NetworkAnnonymityLevel.Relay]: "Relay",
    [NetworkAnnonymityLevel.Proxy]: "Proxy",
    [NetworkAnnonymityLevel.Hosting]: "Hosting",
    [NetworkAnnonymityLevel.VPN]: "VPN",
    [NetworkAnnonymityLevel.TOR]: "TOR",
    [NetworkAnnonymityLevel.All]: "All",
  };

export const NETWORK_ANONYMITY_TOOLTIPS: Record<
  NetworkAnnonymityLevel,
  string
> = {
  [NetworkAnnonymityLevel.None]: "Device has no anonymity",
  [NetworkAnnonymityLevel.Relay]:
    "Traffic detected through service fingerprinting with a confidence score (e.g. iCloud Private Relay)",
  [NetworkAnnonymityLevel.Proxy]:
    "Covers public, residential, and datacenter proxies",
  [NetworkAnnonymityLevel.Hosting]:
    "Traffic detected from data-center/hosting-provider IP ranges rather than residential ISPs",
  [NetworkAnnonymityLevel.VPN]:
    "Traffic detected via active measurement of VPN and proxy exit-node behavior",
  [NetworkAnnonymityLevel.TOR]:
    "Traffic detected via known Tor exit-node infrastructure",
  [NetworkAnnonymityLevel.All]: "",
};

const sortLevels = (levels: NetworkAnnonymityLevel[]) =>
  NETWORK_ANONYMITY_LEVELS.filter((level) => levels.includes(level));

/**
 * Targeting used to be a maximum threshold, so the legacy field means "this
 * level and everything below it". Expanding it here keeps the panel showing
 * what the DSP actually targets until the line item is saved in the new shape.
 * `All` is the legacy "filtering disabled" sentinel, not a selection of all levels.
 */
const expandLegacyThreshold = (
  threshold: NetworkAnnonymityLevel | undefined
): NetworkAnnonymityLevel[] => {
  if (threshold === undefined || threshold === NetworkAnnonymityLevel.All) {
    return [];
  }

  return NETWORK_ANONYMITY_LEVELS.filter((level) => level <= threshold);
};

/**
 * The DSP only falls back to the legacy threshold when the new field is absent,
 * so read them in the same order.
 */
export function decodeNetworkAnonymity(
  networkAnonymity: ILineitemNetworkAnnonymity | undefined
): NetworkAnnonymityLevel[] {
  const allowed = networkAnonymity?.anonymityLevelsAllowed;

  if (Array.isArray(allowed)) {
    return sortLevels(allowed);
  }

  return expandLegacyThreshold(networkAnonymity?.maxAllowableAnonymity);
}

/**
 * An empty selection drops the field. Callers must clear the legacy threshold
 * alongside it, otherwise the DSP falls back to the very targeting the user
 * just removed.
 */
export function encodeNetworkAnonymity(
  levels: NetworkAnnonymityLevel[]
): NetworkAnnonymityLevel[] | undefined {
  const selected = sortLevels(levels);

  return selected.length ? selected : undefined;
}
